/**
 * Fachbegriffe des Ticketsystems – genau einmal definiert.
 *
 * Werte sind englisch und ASCII (Datenbank, API, Code), Beschriftungen deutsch.
 * Bewusst `as const`-Tupel statt `enum`: Node führt TypeScript per Type Stripping
 * aus und kennt keine Enums.
 */

/** Fachliche Rollen, linear: lead > technician > reporter (Plan §9.2). */
export const USER_ROLE = ['reporter', 'technician', 'lead'] as const;
export type UserRole = (typeof USER_ROLE)[number];

export const ROLE_RANK: Record<UserRole, number> = { reporter: 1, technician: 2, lead: 3 };

export const ROLE_LABEL: Record<UserRole, string> = {
  reporter: 'Melder',
  technician: 'Instandhalter',
  lead: 'Instandhaltungsleitung',
};

/** Neun Zustände des Ticket-Automaten (Plan §7). */
export const TICKET_STATUS = [
  'new',
  'accepted',
  'in_progress',
  'waiting_part',
  'waiting_external',
  'waiting_reporter',
  'resolved',
  'closed',
  'rejected',
] as const;
export type TicketStatus = (typeof TICKET_STATUS)[number];

export const STATUS_LABEL: Record<TicketStatus, string> = {
  new: 'Neu',
  accepted: 'Angenommen',
  in_progress: 'In Arbeit',
  waiting_part: 'Wartet auf Teil',
  waiting_external: 'Wartet auf Dienstleister',
  waiting_reporter: 'Rückfrage an Melder',
  resolved: 'Behoben',
  closed: 'Abgeschlossen',
  rejected: 'Abgelehnt',
};

/** Offen = alles außer behoben, abgeschlossen, abgelehnt (F9: `resolved` zählt als „läuft“). */
export const OPEN_STATUSES: readonly TicketStatus[] = [
  'new',
  'accepted',
  'in_progress',
  'waiting_part',
  'waiting_external',
  'waiting_reporter',
];
export const WAITING_STATUSES: readonly TicketStatus[] = [
  'waiting_part',
  'waiting_external',
  'waiting_reporter',
];
export const DONE_STATUSES: readonly TicketStatus[] = ['resolved', 'closed', 'rejected'];

export function isOpenStatus(status: TicketStatus): boolean {
  return OPEN_STATUSES.includes(status);
}

export const PRIORITY = ['p1', 'p2', 'p3', 'p4'] as const;
export type Priority = (typeof PRIORITY)[number];

export const PRIORITY_LABEL: Record<Priority, string> = {
  p1: 'P1 Sicherheit',
  p2: 'P2 Stillstand',
  p3: 'P3 Einschränkung',
  p4: 'P4 Sonstiges',
};

/** Die vier Kacheln beim Melden (F17) – die Wahl des Melders bestimmt die Startpriorität. */
export const TILE = ['safety', 'stop', 'impaired', 'other'] as const;
export type Tile = (typeof TILE)[number];

export const TILE_PRIORITY: Record<Tile, Priority> = {
  safety: 'p1',
  stop: 'p2',
  impaired: 'p3',
  other: 'p4',
};

export const TILE_LABEL: Record<Tile, string> = {
  safety: 'Sicherheit',
  stop: 'Stillstand',
  impaired: 'Einschränkung',
  other: 'Sonstiges',
};

export const TILE_HINT: Record<Tile, string> = {
  safety: 'Gefahr für Personen, Schutzeinrichtung defekt',
  stop: 'Maschine produziert nicht',
  impaired: 'Läuft, aber Ausschuss oder weniger Leistung',
  other: 'Alles andere',
};

export const ORIGIN = ['web', 'qr', 'quick', 'kiosk', 'mail'] as const;
export type TicketOrigin = (typeof ORIGIN)[number];

export const ORIGIN_LABEL: Record<TicketOrigin, string> = {
  web: 'Web',
  qr: 'QR-Code',
  quick: 'Schnellmeldung',
  kiosk: 'Hallen-Tablet',
  mail: 'E-Mail',
};

export const CAUSE_CATEGORY = [
  'mechanical',
  'electrical',
  'operating_error',
  'wear',
  'external',
  'unknown',
] as const;
export type CauseCategory = (typeof CAUSE_CATEGORY)[number];

export const CAUSE_LABEL: Record<CauseCategory, string> = {
  mechanical: 'Mechanik',
  electrical: 'Elektrik',
  operating_error: 'Bedienfehler',
  wear: 'Verschleiß',
  external: 'Fremdeinwirkung',
  unknown: 'Unbekannt',
};

export const REJECT_REASON = ['duplicate', 'not_a_fault', 'spam', 'it_ticket', 'other'] as const;
export type RejectReason = (typeof REJECT_REASON)[number];

export const REJECT_REASON_LABEL: Record<RejectReason, string> = {
  duplicate: 'Dublette',
  not_a_fault: 'Keine Störung',
  spam: 'Spam',
  it_ticket: 'IT-Thema',
  other: 'Sonstiges',
};

export const EVENT_KIND = [
  'created',
  'status',
  'assigned',
  'priority',
  'machine',
  'comment',
  'mail_in',
  'mail_out',
  'attachment',
  'field',
  'notification',
  'system',
] as const;
export type EventKind = (typeof EVENT_KIND)[number];

export const VISIBILITY = ['internal', 'public'] as const;
export type Visibility = (typeof VISIBILITY)[number];

export const MACHINE_STATUS = ['active', 'decommissioned'] as const;
export type MachineStatus = (typeof MACHINE_STATUS)[number];

/** Abgeleiteter Zustand der Ampel (F9) – nie gespeichert. */
export const MACHINE_LIGHT = ['ok', 'impaired', 'down', 'unreviewed', 'decommissioned'] as const;
export type MachineLight = (typeof MACHINE_LIGHT)[number];

export const MACHINE_LIGHT_LABEL: Record<MachineLight, string> = {
  ok: 'Läuft',
  impaired: 'Eingeschränkt',
  down: 'Steht',
  unreviewed: 'Gemeldet, ungeprüft',
  decommissioned: 'Stillgelegt',
};

/** Stammfelder, die der CSV-Import an vorhandenen Maschinen ändern darf; der Bereich kommt gesondert dazu. */
export const MACHINE_IMPORT_FIELD = ['name', 'manufacturer', 'model', 'serialNo', 'yearBuilt'] as const;
export type MachineImportField = (typeof MACHINE_IMPORT_FIELD)[number];

export const MACHINE_IMPORT_FIELD_LABEL: Record<MachineImportField, string> = {
  name: 'Name',
  manufacturer: 'Hersteller',
  model: 'Typ',
  serialNo: 'Seriennummer',
  yearBuilt: 'Baujahr',
};

export const DOCUMENT_KIND = ['manual', 'wiring', 'inspection_report', 'contract', 'other'] as const;
export type DocumentKind = (typeof DOCUMENT_KIND)[number];

export const DOCUMENT_KIND_LABEL: Record<DocumentKind, string> = {
  manual: 'Betriebsanleitung',
  wiring: 'Schaltplan',
  inspection_report: 'Prüfbericht',
  contract: 'Vertrag',
  other: 'Sonstiges',
};

/** Reiter der Systemverwaltung. Die Route prüft die Adresse, ohne die Seite selbst zu laden. */
export const ADMIN_TAB = ['users', 'sites', 'settings', 'mail', 'jobs', 'devices', 'audit'] as const;
export type AdminTab = (typeof ADMIN_TAB)[number];

export const ADMIN_TAB_LABEL: Record<AdminTab, string> = {
  users: 'Benutzer',
  sites: 'Standorte & Bereiche',
  settings: 'Einstellungen',
  mail: 'Postfach',
  jobs: 'Jobs',
  devices: 'Tablets',
  audit: 'Audit-Log',
};

/** Fotos und PDFs zeigt die Oberfläche direkt an; alles andere gibt es nur zum Herunterladen. */
export function isViewableFile(contentType: string): boolean {
  return contentType.startsWith('image/') || contentType === 'application/pdf';
}

/**
 * Behobene und erledigte Tickets eines Teams zeigt die Liste erst ab dieser Zahl
 * aktiver Mitglieder. Bei weniger Personen wäre das Team ein Personenfilter (§14.1).
 */
export const MIN_TEAM_SIZE_FOR_HISTORY = 3;

/** Grenzwerte (F10, F16, F20, Plan §8.5). */
export const LIMITS = {
  imageMaxBytes: 5 * 1024 * 1024,
  documentMaxBytes: 10 * 1024 * 1024,
  mailAttachmentMaxBytes: 10 * 1024 * 1024,
  mailTotalMaxBytes: 30 * 1024 * 1024,
  uploadBodyMaxBytes: 15 * 1024 * 1024,
  attachmentsPerTicket: 10,
  photosOnReport: 3,
  quickCommentMaxChars: 500,
  titleMaxChars: 120,
  descriptionMaxChars: 5000,
  commentMaxChars: 5000,
  mentionsPerComment: 10,
  outboundAttachmentMaxBytes: 3 * 1024 * 1024,
} as const;

/** Ein Wert aus `LIMITS` als Text für Meldungen. */
export function limitInMb(bytes: number): string {
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

/** Meldung an der Körpergrenze der Upload-Routen, im Server und vor dem Senden in der Oberfläche. */
export const UPLOAD_TOO_LARGE_MESSAGE = `Die Datei ist zu groß. Erlaubt sind Fotos bis ${limitInMb(LIMITS.imageMaxBytes * 3)} und Dokumente bis ${limitInMb(LIMITS.documentMaxBytes)}.`;

/** Ticketnummer in Anzeige und Mail-Betreff (Plan §6.5). */
export const TICKET_PREFIX = 'IH-';

export function formatTicketNumber(n: number | string): string {
  return `${TICKET_PREFIX}${n}`;
}

/** `[IH-1234]` im Betreff – robuste Zuordnung von Antworten. */
export const TICKET_TAG_REGEX = /\[IH-(\d{4,})\]/i;

/** Liest „IH-1234“ oder „1234“; alles andere ist `null`. */
export function parseTicketNumber(raw: string): number | null {
  const m = /^(?:IH-)?(\d{4,})$/i.exec(raw.trim());
  return m?.[1] ? Number(m[1]) : null;
}
